import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminContentPageStatus,
  CafeContentDocument,
  CafePageText,
} from '@mds/shared/types/content';

import { centsToInput, inputToCents } from '@/lib/money';
import { dollars, optionalText, requiredText } from '@/lib/validation';

/**
 * The café editor's working copy, and the conversions either side of it.
 *
 * The one difference from the saved document is price: typed dollars stay a
 * string until save, so a half-typed "2." is not rounded away under the cursor.
 * Category and item ids are generated once, when the row is added, and kept
 * through every edit and reorder — the API treats them as stable identities.
 */

export interface ItemDraft {
  id: string;
  name: string;
  description: string;
  price: string;
  imageFileId: string | null;
  isAvailable: boolean;
}

export interface CategoryDraft {
  id: string;
  name: string;
  description: string;
  items: ItemDraft[];
}

export interface CafeDraft {
  page: CafePageText;
  heroImageFileId: string | null;
  galleryFileIds: string[];
  categories: CategoryDraft[];
}

export const LIMITS = { categories: 30, itemsPerCategory: 100, items: 500, gallery: 12 };

export const draftOf = (doc: CafeContentDocument): CafeDraft => ({
  page: { ...doc.page },
  heroImageFileId: doc.heroImageFileId,
  galleryFileIds: [...doc.galleryFileIds],
  categories: doc.categories.map((c) => ({
    id: c.id,
    name: c.name,
    description: c.description,
    items: c.items.map((i) => ({
      id: i.id,
      name: i.name,
      description: i.description,
      price: centsToInput(i.priceAmountMinor),
      imageFileId: i.imageFileId,
      isAvailable: i.isAvailable,
    })),
  })),
});

/** The draft as the API takes it. Check it against `cafeSchema` first. */
export const documentOf = (draft: CafeDraft): CafeContentDocument => ({
  currency: 'USD',
  page: {
    eyebrow: draft.page.eyebrow.trim(),
    title: draft.page.title.trim(),
    summary: draft.page.summary.trim(),
    description: draft.page.description.trim(),
    menuNote: draft.page.menuNote.trim(),
  },
  heroImageFileId: draft.heroImageFileId,
  galleryFileIds: draft.galleryFileIds,
  categories: draft.categories.map((c) => ({
    id: c.id,
    name: c.name.trim(),
    description: c.description.trim(),
    items: c.items.map((i) => ({
      id: i.id,
      name: i.name.trim(),
      description: i.description.trim(),
      priceAmountMinor: inputToCents(i.price) ?? 0,
      imageFileId: i.imageFileId,
      isAvailable: i.isAvailable,
    })),
  })),
});

/**
 * Whether the form differs from the saved draft it was loaded from.
 *
 * Both sides go through the same conversion before comparing. The saved copy
 * comes back from a JSONB column, which reorders object keys (shortest first),
 * so comparing it raw would never match and the form would read as edited
 * forever, and Publish would stay disabled.
 */
export const isDirty = (draft: CafeDraft, saved: CafeContentDocument) =>
  JSON.stringify(documentOf(draft)) !== JSON.stringify(documentOf(draftOf(saved)));

export const blankItem = (): ItemDraft => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  price: '',
  imageFileId: null,
  isAvailable: true,
});

export const blankCategory = (): CategoryDraft => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  items: [blankItem()],
});

/* ---------------------------------------------------------- validation --- */

const itemSchema = z.object({
  name: requiredText(160),
  description: optionalText(1_000),
  price: dollars({ max: 1_000_000_000 }),
});

const categorySchema = z.object({
  name: requiredText(120),
  description: optionalText(500),
  items: z
    .array(itemSchema)
    .max(
      LIMITS.itemsPerCategory,
      `A category holds at most ${LIMITS.itemsPerCategory} items. Move some to another category.`,
    ),
});

/**
 * The café draft as `PUT /admin/content/cafe` would check it — the API's
 * `cafeContentDocumentSchema`. Ids are generated here and not checked.
 */
export const cafeSchema = z
  .object({
    page: z.object({
      eyebrow: optionalText(80),
      title: requiredText(160),
      summary: optionalText(500),
      description: optionalText(20_000),
      menuNote: optionalText(1_000),
    }),
    galleryFileIds: z
      .array(z.string())
      .max(LIMITS.gallery, `The gallery holds at most ${LIMITS.gallery} pictures.`)
      .refine(
        (ids) => new Set(ids).size === ids.length,
        'Each picture can be in the gallery once.',
      ),
    categories: z
      .array(categorySchema)
      .max(LIMITS.categories, `The menu holds at most ${LIMITS.categories} categories.`),
  })
  .superRefine((draft, context) => {
    const total = draft.categories.reduce((n, c) => n + c.items.length, 0);
    if (total > LIMITS.items) {
      context.addIssue({
        code: 'custom',
        path: ['categories'],
        message: `The menu holds at most ${LIMITS.items} items in all. Remove ${total - LIMITS.items}.`,
      });
    }
  });

/* ------------------------------------------------------------- reading --- */

export const statusLabel = (status: AdminContentPageStatus) =>
  status === 'draft'
    ? 'Not published'
    : status === 'published'
      ? 'Published'
      : 'Published · draft has changes';

export const statusTone = (status: AdminContentPageStatus) =>
  status === 'draft'
    ? 'border-hairline bg-raised text-ink-muted'
    : status === 'published'
      ? 'border-brand-edge/35 bg-brand-wash text-brand-strong'
      : 'border-warn-edge bg-warn-wash text-warn-text';

/* -------------------------------------------------------------- errors --- */

const errorBody = (error: unknown) =>
  error instanceof ApiError
    ? (error.body as { code?: string; details?: { fileIds?: string[] } } | null)
    : null;

/** Someone else saved, published or unpublished since this copy was loaded. */
export const isVersionConflict = (error: unknown) =>
  errorBody(error)?.code === 'CONTENT_VERSION_CONFLICT';

/** The stored files that can no longer be shown publicly. */
export const invalidAssets = (error: unknown): string[] =>
  errorBody(error)?.code === 'CONTENT_ASSET_INVALID'
    ? (errorBody(error)?.details?.fileIds ?? [])
    : [];
